/**
 * viewConfig — PURE engine behind first-class saved views.
 *
 * WHY this exists: the auditor flagged that saved views were half-wired —
 * a flat AND-only filter engine (src/lib/crm/viewFilter.ts) plus a store
 * wrapper (src/lib/db/savedViews.ts), but no create/name/pin/share UI, no
 * nested compound filters, no relative dates, and no table↔kanban lensing.
 * This module is the missing core: a rich `SavedView` config object
 * (view type, visibility, pinned, columns, nested AND/OR filter tree,
 * groupBy, sort) plus the pure functions that interpret and edit it.
 *
 * Design contract (mirrors viewFilter.ts):
 * - PURE: no store access, no globals, inputs never mutated.
 * - Generic: records are plain objects; fields are looked up by name.
 * - Honest about missing data: comparisons never phantom-match a row
 *   whose field is absent, and sorting puts missing values last.
 *
 * Persistence stays in src/lib/db/savedViews.ts — `packViewFilters` /
 * `unpackViewFilters` at the bottom adapt this rich config onto that
 * existing API (the whole config rides in one filter clause), so no
 * schema / rules / store changes are needed.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** How the view renders: a column table or a grouped kanban board. */
export type ViewType = 'table' | 'kanban';

/** Who can see the view: just its owner, or the whole team. */
export type Visibility = 'personal' | 'team';

/** One column in a table lens: which field, shown or not, size, position. */
export interface ColumnConfig {
  field: string;
  visible: boolean;
  width: number;
  order: number;
}

/** One comparison clause. `relDate` resolves against a supplied "now". */
export interface FilterLeaf {
  field: string;
  cmp: 'eq' | 'neq' | 'contains' | 'gt' | 'lt' | 'in' | 'relDate';
  value: any;
}

/** A compound group: AND/OR over child groups and leaves — nests freely. */
export interface FilterNode {
  op: 'and' | 'or';
  children: (FilterNode | FilterLeaf)[];
}

/** A first-class, shareable saved view. */
export interface SavedView {
  id: string;
  name: string;
  /** Which record type this view lenses, e.g. "accounts". */
  objectType: string;
  viewType: ViewType;
  visibility: Visibility;
  pinned: boolean;
  ownerId: string;
  columns: ColumnConfig[];
  filter: FilterNode;
  /** Kanban lane field. Seeded when switching to kanban. */
  groupBy?: string;
  sort?: { field: string; dir: 'asc' | 'desc' };
}

// ---------------------------------------------------------------------------
// Filter evaluation
// ---------------------------------------------------------------------------

const DAY_MS = 86_400_000;

/** Is this child a compound group (vs a leaf clause)? */
export function isFilterNode(n: FilterNode | FilterLeaf): n is FilterNode {
  return 'op' in n && (n.op === 'and' || n.op === 'or');
}

/** Parse a field value into epoch ms, or undefined when not a date. */
function toEpoch(v: unknown): number | undefined {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const t = Date.parse(v);
    return Number.isNaN(t) ? undefined : t;
  }
  return undefined;
}

/** Does one record satisfy one leaf clause? */
function matchesLeaf(leaf: FilterLeaf, record: Record<string, any>, now: number): boolean {
  const actual = record?.[leaf.field];
  const expected = leaf.value;

  switch (leaf.cmp) {
    case 'eq':
      return actual === expected;

    case 'neq':
      return actual !== expected;

    case 'contains': {
      if (Array.isArray(actual)) return actual.includes(expected);
      if (typeof actual === 'string') {
        return actual.toLowerCase().includes(String(expected).toLowerCase());
      }
      return false;
    }

    case 'gt':
    case 'lt': {
      const flip = leaf.cmp === 'lt';
      if (typeof actual === 'number' && typeof expected === 'number') {
        return flip ? actual < expected : actual > expected;
      }
      if (typeof actual === 'string' && typeof expected === 'string') {
        return flip ? actual < expected : actual > expected;
      }
      return false;
    }

    case 'in':
      return Array.isArray(expected) && expected.includes(actual);

    case 'relDate': {
      // value: { days, dir? } or a bare number of days (past window).
      const spec =
        typeof expected === 'number'
          ? { days: expected, dir: 'past' as const }
          : (expected as { days?: number; dir?: 'past' | 'future' } | null);
      const days = spec?.days;
      if (typeof days !== 'number' || !Number.isFinite(days)) return false;
      const t = toEpoch(actual);
      if (t === undefined) return false; // missing/invalid date never matches
      if ((spec?.dir ?? 'past') === 'future') {
        // "due in the next N days": now <= t <= now + N days (inclusive).
        return t >= now && t <= now + days * DAY_MS;
      }
      // "in the last N days": now - N days <= t <= now (inclusive).
      return t <= now && t >= now - days * DAY_MS;
    }
  }
}

/**
 * Recursively evaluate a filter tree against one record. An empty AND
 * group is vacuously true (the identity view); an empty OR group is
 * false. `now` defaults to the wall clock so callers with no opinion
 * about time get honest relative dates; tests pass it explicitly.
 */
export function evalFilter(
  node: FilterNode | FilterLeaf,
  record: Record<string, any>,
  now: number = Date.now(),
): boolean {
  if (!isFilterNode(node)) return matchesLeaf(node, record, now);
  if (node.op === 'and') return node.children.every((c) => evalFilter(c, record, now));
  return node.children.some((c) => evalFilter(c, record, now));
}

// ---------------------------------------------------------------------------
// Applying a view: filter + sort + column projection
// ---------------------------------------------------------------------------

/** Visible columns in display order — the table lens's header spec. */
export function visibleColumns(cols: ColumnConfig[]): ColumnConfig[] {
  return cols
    .filter((c) => c.visible)
    .slice()
    .sort((a, b) => a.order - b.order);
}

/** Stable sort with missing values last regardless of direction. Pure. */
export function sortRecords<T extends Record<string, any>>(
  records: T[],
  sort?: { field: string; dir: 'asc' | 'desc' },
): T[] {
  const out = [...records];
  if (!sort) return out;
  const sign = sort.dir === 'desc' ? -1 : 1;
  out.sort((a, b) => {
    const av = a?.[sort.field];
    const bv = b?.[sort.field];
    const aMissing = av === undefined || av === null;
    const bMissing = bv === undefined || bv === null;
    if (aMissing && bMissing) return 0;
    if (aMissing) return 1;
    if (bMissing) return -1;
    let cmp = 0;
    if (typeof av === 'number' && typeof bv === 'number') cmp = av - bv;
    else cmp = String(av).toLowerCase().localeCompare(String(bv).toLowerCase());
    return cmp * sign;
  });
  return out;
}

/**
 * Run a view end-to-end over raw records: filter through the compound
 * tree, order by the view's sort, then project each row down to ONLY
 * the visible columns, in column order (key order = display order).
 * Pure — inputs are never mutated.
 */
export function applyView(
  records: Record<string, any>[],
  view: SavedView,
  now: number,
): Record<string, any>[] {
  const cols = visibleColumns(view.columns);
  const kept = records.filter((r) => evalFilter(view.filter, r, now));
  const ordered = sortRecords(kept, view.sort);
  return ordered.map((r) => {
    const projected: Record<string, any> = {};
    for (const c of cols) projected[c.field] = r[c.field];
    return projected;
  });
}

// ---------------------------------------------------------------------------
// Column editing
// ---------------------------------------------------------------------------

/**
 * Move the column at display index `from` to display index `to`
 * (indices are into the order-sorted list) and renumber `order`
 * 0..n-1. Out-of-range indices are a no-op copy. Pure.
 */
export function reorderColumns(cols: ColumnConfig[], from: number, to: number): ColumnConfig[] {
  const sorted = cols.slice().sort((a, b) => a.order - b.order);
  if (from < 0 || from >= sorted.length || to < 0 || to >= sorted.length || from === to) {
    return sorted.map((c, i) => ({ ...c, order: i }));
  }
  const [moved] = sorted.splice(from, 1);
  sorted.splice(to, 0, moved);
  return sorted.map((c, i) => ({ ...c, order: i }));
}

/** Flip one column's visibility. Unknown fields are a no-op. Pure. */
export function toggleColumn(cols: ColumnConfig[], field: string): ColumnConfig[] {
  return cols.map((c) => (c.field === field ? { ...c, visible: !c.visible } : { ...c }));
}

// ---------------------------------------------------------------------------
// View-type switching (the table↔kanban lens)
// ---------------------------------------------------------------------------

/**
 * Switch a view between table and kanban WITHOUT losing anything the
 * user built: filter, sort, columns and identity are all preserved.
 * Kanban needs a lane field, so switching to kanban seeds `groupBy`
 * (keep an existing choice; otherwise the first visible column).
 * Switching to table drops the groupBy requirement.
 */
export function switchViewType(view: SavedView, t: ViewType): SavedView {
  if (t === 'kanban') {
    const seed = view.groupBy ?? visibleColumns(view.columns)[0]?.field ?? 'stage';
    return { ...view, viewType: 'kanban', groupBy: seed };
  }
  const { groupBy: _dropped, ...rest } = view;
  return { ...rest, viewType: 'table' };
}

// ---------------------------------------------------------------------------
// Sidebar partitioning
// ---------------------------------------------------------------------------

/**
 * Split views into the three sidebar sections. Pinned takes precedence
 * (a pinned view appears ONLY under pinned); the remainder splits into
 * "mine" (personal views the user owns) and "team" (shared views).
 * Personal views owned by someone else are honestly excluded — they
 * were never shared. No view appears twice.
 */
export function partitionViews(
  views: SavedView[],
  userId: string,
): { pinned: SavedView[]; personal: SavedView[]; team: SavedView[] } {
  const pinned: SavedView[] = [];
  const personal: SavedView[] = [];
  const team: SavedView[] = [];
  for (const v of views) {
    const visible = v.visibility === 'team' || v.ownerId === userId;
    if (!visible) continue;
    if (v.pinned) pinned.push(v);
    else if (v.visibility === 'team') team.push(v);
    else personal.push(v);
  }
  return { pinned, personal, team };
}

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

/**
 * The identity view for an object type: every field visible in given
 * order, empty AND filter (matches everything), table lens, personal,
 * unpinned. This is what a list renders before the user saves anything.
 */
export function defaultView(objectType: string, ownerId: string, fields: string[]): SavedView {
  return {
    id: `${objectType}:default`,
    name: 'All records',
    objectType,
    viewType: 'table',
    visibility: 'personal',
    pinned: false,
    ownerId,
    columns: fields.map((field, i) => ({ field, visible: true, width: 160, order: i })),
    filter: { op: 'and', children: [] },
  };
}

// ---------------------------------------------------------------------------
// Persistence adapter (rides the existing savedViews store API)
// ---------------------------------------------------------------------------

/** The clause shape src/lib/db/savedViews.ts persists. */
interface StoredClause {
  field: string;
  op: 'eq' | 'neq' | 'contains' | 'gt' | 'lt' | 'in';
  value: unknown;
}

/** Sentinel field name carrying a full rich view config in one clause. */
export const VIEW_CONFIG_FIELD = '__viewConfig';

/**
 * Pack a rich view into the legacy `ViewFilter[]` payload so it can be
 * persisted through the EXISTING savedViews API with zero schema
 * changes. The whole config rides as one JSON-safe clause value.
 */
export function packViewFilters(view: SavedView): StoredClause[] {
  return [{ field: VIEW_CONFIG_FIELD, op: 'eq', value: { ...view } }];
}

/**
 * Recover a rich view from a persisted filter payload. Returns null for
 * legacy (flat-filter) records so callers can skip them honestly rather
 * than fabricate a config.
 */
export function unpackViewFilters(filters: Array<{ field: string; value: unknown }>): SavedView | null {
  const clause = filters.find((f) => f.field === VIEW_CONFIG_FIELD);
  if (!clause || clause.value === null || typeof clause.value !== 'object') return null;
  const v = clause.value as Partial<SavedView>;
  if (
    typeof v.name !== 'string' ||
    typeof v.objectType !== 'string' ||
    (v.viewType !== 'table' && v.viewType !== 'kanban') ||
    !Array.isArray(v.columns) ||
    !v.filter ||
    typeof v.filter !== 'object'
  ) {
    return null;
  }
  return {
    id: typeof v.id === 'string' ? v.id : `${v.objectType}:${v.name}`,
    name: v.name,
    objectType: v.objectType,
    viewType: v.viewType,
    visibility: v.visibility === 'team' ? 'team' : 'personal',
    pinned: v.pinned === true,
    ownerId: typeof v.ownerId === 'string' ? v.ownerId : '',
    columns: v.columns as ColumnConfig[],
    filter: v.filter as FilterNode,
    ...(typeof v.groupBy === 'string' ? { groupBy: v.groupBy } : {}),
    ...(v.sort ? { sort: v.sort } : {}),
  };
}
